import { drawing as d } from '@progress/kendo-drawing';
import { PathDefiner, Point, Rect } from './../math';
import { defined, isDefined, isFunction, isString, remove } from './../utils';
import { deepExtend } from './../utils/deep-extend';
import { closestConnector, filterConnectionDataItem, isAutoConnector } from './utils';
import { CascadingRouter } from './../services/routing/CascadingRouter';
import { AUTO, BOTTOM, CASCADING, CONNECTION_CONTENT_OFFSET, Cursors, INLINE, LEFT, MAXINT, NONE, RIGHT, SOURCE, TARGET, TOP, TRANSPARENT, FOCUS_CLASS, CLASS_NAME, EMPTY, CONNECTION } from './../constants';
import { Connector } from './Connector';
import { PolylineRouter } from './../services/routing/PolylineRouter';
import { ConnectionEditAdorner } from './../services/adorners/ConnectionEditAdorner';
import { CascadingConnectionResizeAdorner } from '../services/adorners/ConnectionResizeAdorner/CascadingConnection';
import { PolylineConnectionResizeAdorner } from './../services/adorners/ConnectionResizeAdorner/PolylineConnection';
import { Polyline } from './../svg';
import { ConnectionEditUnit } from './../services/ConnectionEditUnit';
import { Shape } from './Shape';
import { DiagramElement, getTextElementType } from './DiagramElement';
import { getTemplate } from "../services/template/get-template";
import { extend } from '../utils/scroller/utils';
const defaultOptions = {
    hover: {
        stroke: {}
    },
    startCap: NONE,
    endCap: NONE,
    cornerRadius: 0,
    anchorOffset: 20,
    points: [],
    selectable: true,
    fromConnector: AUTO,
    toConnector: AUTO
};
const connectionTextOptions = (options) => {
    return {
        ...options,
        padding: undefined,
        textPadding: options.padding,
        background: options.position === INLINE ? options.background || options.bgColor : options.background
    };
};
/**
 * The visual link between two Shapes through the intermediate of Connectors.
 */
export class Connection extends DiagramElement {
    /**
     * Creates a new Connection instance.
     * @param from The source endpoint (Shape, Connector, or Point)
     * @param to The target endpoint (Shape, Connector, or Point)
     * @param options Configuration options for the connection
     */
    constructor(from, to, options = {}) {
        options = deepExtend({}, defaultOptions, options);
        super(options);
        /** @hidden */
        this.name = CONNECTION;
        /** @hidden */
        this.previewState = { lastSide: null };
        /** @hidden */
        this.routingMode = 'auto';
        this.updateOptionsFromModel();
        this._initRouter();
        this.path = new Polyline(this.options);
        this.path.fill(TRANSPARENT);
        this.visual.append(this.path);
        this._sourcePoint = this._targetPoint = new Point();
        this._setSource(from);
        this._setTarget(to);
        this.content(this.options.content);
        this.definers = [];
        if (options.points) {
            this.points(options.points);
        }
        if (options.locks) {
            this.locks = options.locks.map(lock => ({ ...lock }));
        }
        else {
            this.locks = [];
        }
    }
    /** @hidden */
    _getCursor(point) {
        // Check resize adorner first (for handle hit test)
        if (this.resizeAdorner) {
            const resizeCursor = this.resizeAdorner._getCursor(point);
            if (resizeCursor !== Cursors.select) {
                return resizeCursor;
            }
        }
        // Then check edit adorner
        if (this.adorner) {
            return this.adorner._getCursor(point);
        }
        return this.options.cursor || Cursors.select;
    }
    /** @hidden */
    _setOptionsFromModel(model) {
        this.updateOptionsFromModel(model || this.dataItem);
    }
    /**
     * Updates the connection options from the model data.
     * @param model The model data to extract options from
     */
    updateOptionsFromModel(model) {
        if (this.diagram && this.diagram._isEditable) {
            const dataMap = this.diagram._dataMap;
            const options = filterConnectionDataItem(model || this.dataItem);
            if (model) {
                if (defined(options.from)) {
                    let from = dataMap[options.from];
                    if (from && defined(options.fromConnector)) {
                        from = from.getConnector(options.fromConnector);
                    }
                    this.source(from);
                }
                else if (defined(options.fromX) && defined(options.fromY)) {
                    this.source(new Point(options.fromX, options.fromY));
                }
                if (defined(options.to)) {
                    let to = dataMap[options.to];
                    if (to && defined(options.toConnector)) {
                        to = to.getConnector(options.toConnector);
                    }
                    this.target(to);
                }
                else if (defined(options.toX) && defined(options.toY)) {
                    this.target(new Point(options.toX, options.toY));
                }
                if (defined(options.type) && this.type() !== options.type) {
                    this.points([]);
                    this.type(options.type);
                }
                this.dataItem = model;
                this._template();
                this.redraw(this.options);
            }
            else {
                this.options = deepExtend({}, options, this.options);
            }
        }
    }
    /**
     * Updates the connection's model data and optionally synchronizes changes.
     * @param syncChanges Whether to synchronize changes immediately
     */
    updateModel(syncChanges) {
        if (this.diagram && this.diagram._isEditable) {
            this.diagram.updateConnectionModel(this, syncChanges);
        }
    }
    /**
     * Gets the Point where the source of the connection resides.
     * If the endpoint is Auto-connector the location of the resolved connector will be returned.
     * If the endpoint is floating the location of the endpoint is returned.
     * @returns The source point of the connection
     */
    sourcePoint() {
        return this._resolvedSourceConnector ? this._resolvedSourceConnector.positionWithoutOffset() : this._sourcePoint;
    }
    /** @hidden */
    _setSource(source) {
        const shapeSource = source instanceof Shape;
        const defaultConnector = this.options.fromConnector || AUTO;
        let dataItem;
        if (shapeSource && !source.getConnector(defaultConnector)) {
            return;
        }
        if (source !== undefined) {
            this.from = source;
        }
        this._removeFromSourceConnector();
        if (source === null) { // detach
            if (this.sourceConnector) {
                this._sourcePoint = (this._resolvedSourceConnector || this.sourceConnector).positionWithoutOffset();
                this._clearSourceConnector();
                this._setFromOptions(null, this._sourcePoint);
            }
        }
        else if (source instanceof Connector) {
            dataItem = source.shape.dataItem;
            if (dataItem) {
                this._setFromOptions(dataItem.id);
            }
            this.sourceConnector = source;
            this.sourceConnector.connections.push(this);
        }
        else if (source instanceof Point) {
            this._setFromOptions(null, source);
            this._sourcePoint = source;
            if (this.sourceConnector) {
                this._clearSourceConnector();
            }
        }
        else if (shapeSource) {
            dataItem = source.dataItem;
            if (dataItem) {
                this._setFromOptions(dataItem.id);
            }
            this.sourceConnector = source.getConnector(defaultConnector);
            this.sourceConnector.connections.push(this);
        }
    }
    /**
     * Gets or sets the source endpoint of the connection.
     * @param source The source endpoint (Shape, Connector, or Point) to set. If not provided, returns the current source.
     * @param undoable Whether this operation should be undoable
     * @returns The current source when used as a getter
     */
    source(source, undoable) {
        if (isDefined(source)) {
            if (undoable && this.diagram) {
                this.diagram.undoRedoService.addCompositeItem(new ConnectionEditUnit(this, source));
            }
            this._setSource(source);
            this.refresh();
        }
        return this.sourceConnector ? this.sourceConnector : this._sourcePoint;
    }
    /** @hidden */
    _setFromOptions(from, fromPoint) {
        this.options.from = from;
        if (fromPoint) {
            this.options.fromX = fromPoint.x;
            this.options.fromY = fromPoint.y;
        }
        else {
            this.options.fromX = null;
            this.options.fromY = null;
        }
    }
    /**
     * Gets or sets the PathDefiner of the sourcePoint.
     * The left part of this definer is always null since it defines the source tangent.
     * @param value The PathDefiner to set. If not provided, returns the current source definer.
     * @returns The source definer when used as a getter
     */
    sourceDefiner(value) {
        if (value) {
            if (value instanceof PathDefiner) {
                value.left = null;
                this._sourceDefiner = value;
                this.source(value.point); // refresh implicit here
            }
            else {
                throw new Error('The sourceDefiner needs to be a PathDefiner.');
            }
        }
        else {
            if (!this._sourceDefiner) {
                this._sourceDefiner = new PathDefiner(this.sourcePoint(), null, null);
            }
            return this._sourceDefiner;
        }
    }
    /**
     * Gets the Point where the target of the connection resides.
     * @returns The target point of the connection
     */
    targetPoint() {
        return this._resolvedTargetConnector ? this._resolvedTargetConnector.positionWithoutOffset() : this._targetPoint;
    }
    /** @hidden */
    _setTarget(target) {
        const shapeTarget = target instanceof Shape;
        const defaultConnector = this.options.toConnector || AUTO;
        let dataItem;
        if (shapeTarget && !target.getConnector(defaultConnector)) {
            return;
        }
        if (target !== undefined) {
            this.to = target;
        }
        this._removeFromTargetConnector();
        if (target === null) { // detach
            if (this.targetConnector) {
                this._targetPoint = (this._resolvedTargetConnector || this.targetConnector).positionWithoutOffset();
                this._clearTargetConnector();
                this._setToOptions(null, this._targetPoint);
            }
        }
        else if (target instanceof Connector) {
            dataItem = target.shape.dataItem;
            if (dataItem) {
                this._setToOptions(dataItem.id);
            }
            this.targetConnector = target;
            this.targetConnector.connections.push(this);
        }
        else if (target instanceof Point) {
            this._setToOptions(null, target);
            this._targetPoint = target;
            if (this.targetConnector) {
                this._clearTargetConnector();
            }
        }
        else if (shapeTarget) {
            dataItem = target.dataItem;
            if (dataItem) {
                this._setToOptions(dataItem.id);
            }
            this.targetConnector = target.getConnector(defaultConnector);
            this.targetConnector.connections.push(this);
        }
    }
    /**
     * Gets or sets the target endpoint of the connection.
     * @param target The target endpoint (Shape, Connector, or Point) to set. If not provided, returns the current target.
     * @param undoable Whether this operation should be undoable
     * @returns The current target when used as a getter
     */
    target(target, undoable) {
        if (isDefined(target)) {
            if (undoable && this.diagram) {
                this.diagram.undoRedoService.addCompositeItem(new ConnectionEditUnit(this, undefined, target));
            }
            this._setTarget(target);
            this.refresh();
        }
        return this.targetConnector ? this.targetConnector : this._targetPoint;
    }
    /** @hidden */
    _setToOptions(to, toPoint) {
        this.options.to = to;
        if (toPoint) {
            this.options.toX = toPoint.x;
            this.options.toY = toPoint.y;
        }
        else {
            this.options.toX = null;
            this.options.toY = null;
        }
    }
    /**
     * Gets or sets the PathDefiner of the targetPoint.
     * The right part of this definer is always null since it defines the target tangent.
     * @param value The PathDefiner to set. If not provided, returns the current target definer.
     * @returns The target definer when used as a getter
     */
    targetDefiner(value) {
        if (value) {
            if (value instanceof PathDefiner) {
                value.right = null;
                this._targetDefiner = value;
                this.target(value.point); // refresh implicit here
            }
            else {
                throw new Error('The sourceDefiner needs to be a PathDefiner.');
            }
        }
        else {
            if (!this._targetDefiner) {
                this._targetDefiner = new PathDefiner(this.targetPoint(), null, null);
            }
            return this._targetDefiner;
        }
    }
    /** @hidden */
    _updateConnectors() {
        this._updateConnector(this.source(), SOURCE);
        this._updateConnector(this.target(), TARGET);
    }
    /** @hidden */
    _updateConnector(instance, name) {
        const diagram = this.diagram;
        if (instance instanceof Connector && !diagram.getShapeById(instance.shape.id)) {
            const dataItem = instance.shape.dataItem;
            const connectorName = instance.options.name;
            const setNewTarget = () => {
                const shape = diagram._dataMap[dataItem.id];
                instance = shape.getConnector(connectorName);
                this[name](instance, false);
                this.updateModel();
            };
            if (diagram._dataMap[dataItem.id]) {
                setNewTarget();
            }
            else {
                const inactiveItem = diagram._inactiveShapeItems.getByUid(dataItem.uid);
                if (inactiveItem) {
                    diagram._deferredConnectionUpdates.push(inactiveItem.onActivate(setNewTarget));
                }
            }
        }
        else {
            this[name](instance, false);
        }
    }
    /**
     * Gets or sets the content of the connection.
     * @param content The content to set. If not provided, returns the current content.
     * @returns The current content when used as a getter
     */
    content(content) {
        const current = this.options.content.text;
        const result = this._content(content);
        if (defined(content)) {
            if (current !== result) {
                this._contentVisual._measured = false;
            }
            this._alignContent();
        }
        return result;
    }
    /** @hidden */
    _createContentVisual(options) {
        let visual;
        const hasVisual = options.visual;
        const hasTemplate = options.template;
        const templateOptions = extend({}, options, {
            dataItem: this.dataItem || options.dataItem,
        });
        const template = getTemplate(templateOptions);
        if (hasVisual) {
            const visualResult = isFunction(template) ?
                template.call(this, templateOptions) :
                null;
            visual = visualResult;
        }
        else if (hasTemplate) {
            const templateResult = isFunction(template) ?
                template.call(this, templateOptions.dataItem || {}) :
                null;
            if (isFunction(templateResult)) {
                visual = templateResult;
            }
            else if (isString(templateResult)) {
                const textBlockOptions = {
                    ...templateOptions,
                    text: templateResult
                };
                visual = new (getTextElementType(textBlockOptions))(connectionTextOptions(textBlockOptions));
            }
        }
        else if (options.text) {
            visual = new (getTextElementType(options))(connectionTextOptions(options));
        }
        if (visual) {
            this._contentVisual = visual;
            visual._includeInBBox = false;
            this.visual.append(visual);
        }
        return visual;
    }
    /** @hidden */
    _updateContentVisual(options) {
        if (isFunction(options.visual)) {
            this.visual.remove(this._contentVisual);
            this._createContentVisual(options);
        }
        else {
            this._contentVisual.redraw(options);
        }
    }
    /** @hidden */
    _alignContent() {
        if (this._contentVisual) {
            const contentOptions = this.options.content || {};
            const offset = contentOptions.offset !== undefined ? contentOptions.offset : CONNECTION_CONTENT_OFFSET;
            const points = this.allPoints();
            let endIdx = Math.floor(points.length / 2);
            let startIdx = endIdx - 1;
            while (startIdx > 0 && points[startIdx].equals(points[endIdx])) {
                startIdx--;
                endIdx++;
            }
            let endPoint = points[endIdx];
            let startPoint = points[startIdx];
            const boundingBox = this._contentVisual._measure();
            let width = boundingBox.width;
            let height = boundingBox.height;
            let alignToPath = points.length % 2 === 0;
            const distance = startPoint.distanceTo(endPoint);
            // For inline labels with MultiLineTextBlock, use text-only size for centering
            const position = contentOptions.position || {};
            const verticalPosition = position.vertical || TOP.toLowerCase();
            const horizontalPosition = position.horizontal || RIGHT.toLowerCase();
            const isInline = position === INLINE;
            let originOffsetX = 0;
            let originOffsetY = 0;
            if ((isInline || contentOptions.border) && this._contentVisual._textOnlyRect) {
                const textRect = this._contentVisual._textOnlyRect;
                width = textRect.size.width;
                height = textRect.size.height;
                originOffsetX = textRect.origin.x;
                originOffsetY = textRect.origin.y;
            }
            if (alignToPath && points.length > 2 && distance > 0 &&
                ((startPoint.y === endPoint.y && distance < width) || (startPoint.x === endPoint.x && distance < height))) {
                alignToPath = false;
            }
            const left = LEFT.toLowerCase();
            const bottom = BOTTOM.toLowerCase();
            let point;
            if (alignToPath) {
                const angle = d.util.deg(Math.atan2(endPoint.y - startPoint.y, endPoint.x - startPoint.x));
                point = new Point((endPoint.x - startPoint.x) / 2 + startPoint.x, (endPoint.y - startPoint.y) / 2 + startPoint.y);
                if (isInline) {
                    // For inline, center the label on the connection
                    // Compensate for text origin offset
                    point.x -= (width / 2 + originOffsetX);
                    point.y -= (height / 2 + originOffsetY);
                }
                else if (Math.abs(angle) === 90) {
                    // Vertical connection - use horizontal position
                    if (horizontalPosition === left) {
                        point.x -= width + offset;
                    }
                    else {
                        point.x += offset;
                    }
                    point.y -= height / 2;
                }
                else if (angle % 180 === 0) {
                    // Horizontal connection - use vertical position
                    point.x -= width / 2;
                    if (verticalPosition === bottom) {
                        point.y += offset;
                    }
                    else {
                        point.y -= height + offset;
                    }
                }
                else if (angle < -90 || (0 < angle && angle < 90)) {
                    point.y -= height;
                }
                else if (angle < 0 || angle > 90) {
                    point.x -= width;
                    point.y -= height;
                }
            }
            else {
                const midIdx = Math.floor(points.length / 2);
                point = points[midIdx].clone();
                startPoint = points[midIdx - 1];
                endPoint = points[midIdx + 1];
                // Check if this is a vertical or horizontal segment
                const isVertical = startPoint.x === point.x && endPoint.x === point.x;
                const isHorizontal = startPoint.y === point.y && endPoint.y === point.y;
                let offsetX, offsetY;
                if (isInline) {
                    // For inline, center the label on the connection
                    // Compensate for text origin offset
                    offsetX = -(width / 2 + originOffsetX);
                    offsetY = -(height / 2 + originOffsetY);
                }
                else if (isVertical) {
                    // Vertical segment - use horizontal position
                    if (horizontalPosition === left) {
                        offsetX = -boundingBox.width - offset;
                    }
                    else {
                        offsetX = offset;
                    }
                    offsetY = -boundingBox.height / 2;
                }
                else if (isHorizontal) {
                    // Horizontal segment - use vertical position
                    offsetX = -boundingBox.width / 2;
                    if (verticalPosition === bottom) {
                        offsetY = offset;
                    }
                    else {
                        offsetY = -boundingBox.height - offset;
                    }
                }
                else {
                    // Diagonal or other - keep original logic
                    offsetX = startPoint.x <= point.x && endPoint.x <= point.x ? offset : -boundingBox.width - offset;
                    offsetY = startPoint.y <= point.y && endPoint.y <= point.y ? offset : -boundingBox.height - offset;
                }
                point.x += offsetX;
                point.y += offsetY;
            }
            this._contentVisual.position(point);
        }
    }
    /**
     * Selects or deselects this connection.
     * @param value True to select, false to deselect the connection
     * @returns True if the selection state changed, false otherwise
     */
    select(value) {
        var _a;
        const diagram = this.diagram;
        let selected, deselected;
        if (this._canSelect()) {
            if (this.isSelected !== value) {
                this.isSelected = value;
                selected = [];
                deselected = [];
                if (this.isSelected) {
                    this.adorner = new ConnectionEditAdorner(this, this.options.selection);
                    diagram._adorn(this.adorner, true);
                    const type = (this.options.type || '').toLowerCase();
                    const ResizeAdorner = type === CASCADING ? CascadingConnectionResizeAdorner : PolylineConnectionResizeAdorner;
                    this.resizeAdorner = new ResizeAdorner(this, (_a = this.options.editable) === null || _a === void 0 ? void 0 : _a.points);
                    diagram._adorn(this.resizeAdorner, true);
                    diagram._selectedItems.push(this);
                    selected.push(this);
                }
                else {
                    if (this.adorner) {
                        diagram._adorn(this.adorner, false);
                        this.adorner = undefined;
                    }
                    if (this.resizeAdorner) {
                        diagram._adorn(this.resizeAdorner, false);
                        this.resizeAdorner = undefined;
                    }
                    remove(diagram._selectedItems, this);
                    deselected.push(this);
                }
                if (this.adorner) {
                    this.adorner.refresh();
                }
                if (this.resizeAdorner) {
                    this.resizeAdorner.refresh();
                }
                if (!diagram._internalSelection) {
                    diagram._selectionChanged(selected, deselected);
                }
                return true;
            }
        }
    }
    /**
     * Gets or sets the bounds of this connection.
     * @param value A Rect object to set as bounds. If not provided, returns the current bounds.
     * @returns The bounds of the connection
     * @remarks This is automatically set in the refresh() method.
     */
    bounds(value) {
        if (value && !isString(value)) {
            this._bounds = value;
        }
        else {
            return this._bounds;
        }
    }
    /**
     * Gets or sets the connection type (see ConnectionType enumeration).
     * @param value A ConnectionType value to set. If not provided, returns the current type.
     * @returns The connection type
     */
    type(value) {
        const options = this.options;
        if (value) {
            if (value !== options.type) {
                options.type = value;
                this._initRouter();
                this.refresh();
            }
        }
        else {
            return options.type;
        }
    }
    /** @hidden */
    _initRouter() {
        const type = (this.options.type || '').toLowerCase();
        if (type === CASCADING) {
            this._router = new CascadingRouter(this);
        }
        else {
            this._router = new PolylineRouter(this);
        }
    }
    /**
     * Gets or sets the collection of intermediate points.
     * The 'allPoints()' property will return all the points.
     * The 'definers' property returns the definers of the intermediate points.
     * The 'sourceDefiner' and 'targetDefiner' return the definers of the endpoints.
     * @param value Array of Points or point-like objects to set as intermediate points. If not provided, returns the current points.
     * @returns Array of intermediate points when used as a getter
     */
    points(value) {
        if (value) {
            this.definers = [];
            for (let i = 0; i < value.length; i++) {
                const definition = value[i];
                if (definition instanceof Point) {
                    this.definers.push(new PathDefiner(definition));
                }
                else if (Object.prototype.hasOwnProperty.call(definition, 'x') && Object.prototype.hasOwnProperty.call(definition, 'y')) { // e.g. Clipboard does not preserve the Point definition and turned into an Object
                    this.definers.push(new PathDefiner(new Point(definition.x, definition.y)));
                }
                else {
                    throw new Error('A Connection point needs to be a Point or an object with x and y properties.');
                }
            }
        }
        else {
            const pts = [];
            if (isDefined(this.definers)) {
                for (let k = 0; k < this.definers.length; k++) {
                    pts.push(this.definers[k].point);
                }
            }
            return pts;
        }
    }
    /**
     * Gets all the points of this connection. This is the combination of the sourcePoint, the points and the targetPoint.
     * @returns Array of all points including source, intermediate, and target points
     */
    allPoints() {
        const pts = [this.sourcePoint()];
        if (this.definers) {
            for (let k = 0; k < this.definers.length; k++) {
                pts.push(this.definers[k].point);
            }
        }
        pts.push(this.targetPoint());
        return pts;
    }
    /**
     * Refreshes the connection's visual representation.
     * Resolves connectors, refreshes the path, aligns content, and updates adorners.
     */
    refresh() {
        this._resolveConnectors();
        this._refreshPath();
        this._alignContent();
        if (this.adorner) {
            this.adorner.refresh();
        }
        if (this.resizeAdorner) {
            this.resizeAdorner.refresh();
        }
    }
    /** @hidden */
    _resolveConnectors(destination) {
        const self = destination || this;
        // Clear resolved connectors before recalculating
        self._resolvedSourceConnector = undefined;
        self._resolvedTargetConnector = undefined;
        const source = this.source();
        const target = this.target();
        const sourceReferencePoint = target instanceof Point ? target : null;
        const targetReferencePoint = source instanceof Point ? source : null;
        // Resolve source connector
        if (source instanceof Connector) {
            const sourceConnectors = isAutoConnector(source) ? source.shape.connectors : [source];
            if (sourceReferencePoint && isAutoConnector(source)) {
                self._resolvedSourceConnector = closestConnector(sourceReferencePoint, sourceConnectors);
            }
            else if (!isAutoConnector(source)) {
                // For non-auto connectors, use the connector itself as the resolved connector
                self._resolvedSourceConnector = source;
            }
        }
        // Resolve target connector
        if (target instanceof Connector) {
            const targetConnectors = isAutoConnector(target) ? target.shape.connectors : [target];
            if (targetReferencePoint && isAutoConnector(target)) {
                self._resolvedTargetConnector = closestConnector(targetReferencePoint, targetConnectors);
            }
            else if (!isAutoConnector(target)) {
                // For non-auto connectors, use the connector itself as the resolved connector
                self._resolvedTargetConnector = target;
            }
        }
        // Resolve auto connectors when both endpoints are connectors (and no reference points)
        if (!sourceReferencePoint && !targetReferencePoint &&
            source instanceof Connector && target instanceof Connector) {
            const sourceConnectors = isAutoConnector(source) ? source.shape.connectors : [source];
            const targetConnectors = isAutoConnector(target) ? target.shape.connectors : [target];
            this._resolveAutoConnectors(sourceConnectors, targetConnectors, destination);
        }
    }
    /** @hidden */
    _resolveAutoConnectors(sourceConnectors, targetConnectors, destination) {
        const self = destination || this;
        // Use path-based resolution for:
        // 1. Cascading connections with manual routing (locks)
        // 2. Polyline connections with edited points
        if (this.pointsEditable() && (this.routingMode === 'manual' || this._hasPolylineEditedPoints())) {
            this._resolveAutoConnectorsWithLocks(sourceConnectors, targetConnectors, destination);
            return;
        }
        let minNonConflict = MAXINT;
        let minDist = MAXINT;
        let minNonConflictSource, minNonConflictTarget;
        let sourcePoint, targetPoint;
        let minSource, minTarget;
        let sourceConnector, targetConnector;
        let sourceIdx, targetIdx;
        let dist;
        for (sourceIdx = 0; sourceIdx < sourceConnectors.length; sourceIdx++) {
            sourceConnector = sourceConnectors[sourceIdx];
            if (!isAutoConnector(sourceConnector)) {
                sourcePoint = sourceConnector.positionWithoutOffset();
                for (targetIdx = 0; targetIdx < targetConnectors.length; targetIdx++) {
                    targetConnector = targetConnectors[targetIdx];
                    if (!isAutoConnector(targetConnector)) {
                        targetPoint = targetConnector.positionWithoutOffset();
                        dist = Math.round(sourcePoint.distanceTo(targetPoint));
                        if (dist < minNonConflict && this.diagram && this._testRoutePoints(sourcePoint, targetPoint, sourceConnector, targetConnector)) {
                            minNonConflict = dist;
                            minNonConflictSource = sourceConnector;
                            minNonConflictTarget = targetConnector;
                        }
                        if (dist < minDist) {
                            minSource = sourceConnector;
                            minTarget = targetConnector;
                            minDist = dist;
                        }
                    }
                }
            }
        }
        if (minNonConflictSource) {
            minSource = minNonConflictSource;
            minTarget = minNonConflictTarget;
        }
        self._resolvedSourceConnector = minSource;
        self._resolvedTargetConnector = minTarget;
    }
    /** @hidden */
    _hasPolylineEditedPoints() {
        // For polyline connections, check if intermediate points have been added
        return this._router instanceof PolylineRouter && this.definers && this.definers.length > 0;
    }
    /** @hidden */
    _resolveAutoConnectorsWithLocks(sourceConnectors, targetConnectors, destination) {
        const self = destination || this;
        let minPathLength = MAXINT;
        let minNonConflictPathLength = MAXINT;
        let minSource, minTarget;
        let minNonConflictSource, minNonConflictTarget;
        for (let sourceIdx = 0; sourceIdx < sourceConnectors.length; sourceIdx++) {
            const sourceConnector = sourceConnectors[sourceIdx];
            if (isAutoConnector(sourceConnector))
                continue;
            const sourcePoint = sourceConnector.positionWithoutOffset();
            for (let targetIdx = 0; targetIdx < targetConnectors.length; targetIdx++) {
                const targetConnector = targetConnectors[targetIdx];
                if (isAutoConnector(targetConnector))
                    continue;
                const targetPoint = targetConnector.positionWithoutOffset();
                // Calculate actual route length with locks
                const pathLength = this._calculateRouteLength(sourcePoint, targetPoint, sourceConnector, targetConnector);
                // Check for conflicts if using CascadingRouter
                const hasNoConflict = this.diagram && this._testRoutePoints(sourcePoint, targetPoint, sourceConnector, targetConnector);
                if (hasNoConflict && pathLength < minNonConflictPathLength) {
                    minNonConflictPathLength = pathLength;
                    minNonConflictSource = sourceConnector;
                    minNonConflictTarget = targetConnector;
                }
                if (pathLength < minPathLength) {
                    minPathLength = pathLength;
                    minSource = sourceConnector;
                    minTarget = targetConnector;
                }
            }
        }
        // Prefer non-conflict route if found
        if (minNonConflictSource) {
            self._resolvedSourceConnector = minNonConflictSource;
            self._resolvedTargetConnector = minNonConflictTarget;
        }
        else {
            self._resolvedSourceConnector = minSource;
            self._resolvedTargetConnector = minTarget;
        }
    }
    /** @hidden */
    _calculateRouteLength(sourcePoint, targetPoint, sourceConnector, targetConnector) {
        const router = this._router;
        if (router instanceof CascadingRouter) {
            // Get the actual route points with locks
            const points = router.routePoints(sourcePoint, targetPoint, sourceConnector, targetConnector);
            // Calculate total path length including source and target
            const allPoints = [sourcePoint, ...points, targetPoint];
            let totalLength = 0;
            for (let i = 1; i < allPoints.length; i++) {
                totalLength += allPoints[i - 1].distanceTo(allPoints[i]);
            }
            return Math.round(totalLength);
        }
        else if (this._hasPolylineEditedPoints()) {
            // For polyline with edited points, calculate path through intermediate points
            const intermediatePoints = this.definers.map(d => d.point);
            const allPoints = [sourcePoint, ...intermediatePoints, targetPoint];
            let totalLength = 0;
            for (let i = 1; i < allPoints.length; i++) {
                totalLength += allPoints[i - 1].distanceTo(allPoints[i]);
            }
            return Math.round(totalLength);
        }
        // Fallback to straight line distance if no intermediate points
        return Math.round(sourcePoint.distanceTo(targetPoint));
    }
    /** @hidden */
    _testRoutePoints(sourcePoint, targetPoint, sourceConnector, targetConnector) {
        const router = this._router;
        let passRoute = true;
        if (router instanceof CascadingRouter) {
            const points = router.routePoints(sourcePoint, targetPoint, sourceConnector, targetConnector), exclude = this._getRouteExclude(sourcePoint, targetPoint, sourceConnector.shape, targetConnector.shape);
            let start, end, rect;
            points.unshift(sourcePoint);
            points.push(targetPoint);
            for (let idx = 1; idx < points.length; idx++) {
                start = points[idx - 1];
                end = points[idx];
                rect = new Rect(Math.min(start.x, end.x), Math.min(start.y, end.y), Math.abs(start.x - end.x), Math.abs(start.y - end.y));
                if (rect.width > 0) {
                    rect.x++;
                    rect.width -= 2;
                }
                if (rect.height > 0) {
                    rect.y++;
                    rect.height -= 2;
                }
                if (!rect.isEmpty() && this.diagram._shapesQuadTree.hitTestRect(rect, exclude)) {
                    passRoute = false;
                    break;
                }
            }
        }
        return passRoute;
    }
    /** @hidden */
    _getRouteExclude(sourcePoint, targetPoint, sourceShape, targetShape) {
        const exclude = [];
        if (this._isPointInsideShape(sourcePoint, sourceShape)) {
            exclude.push(sourceShape);
        }
        if (this._isPointInsideShape(targetPoint, targetShape)) {
            exclude.push(targetShape);
        }
        return exclude;
    }
    /** @hidden */
    _isPointInsideShape(point, shape) {
        const bounds = shape.bounds(), angle = shape.rotate().angle, boundsX = bounds.x, boundsY = bounds.y;
        const rotatedPoint = point.clone().rotate(angle, bounds.center());
        const pointX = rotatedPoint.x;
        const pointY = rotatedPoint.y;
        return pointX > boundsX && pointX < (boundsX + bounds.width) && pointY > boundsY && pointY < (boundsY + bounds.height);
    }
    /**
     * Redraws the connection with new options.
     * @param options The options to apply when redrawing the connection
     */
    redraw(options) {
        if (options) {
            this.options = deepExtend({}, this.options, options);
            const points = this.options.points;
            if (defined(points) && points.length > 0) {
                this.points(points);
                this._refreshPath();
            }
            if ((options && options.content) || options.text) {
                this.content(options.content);
            }
            this.path.redraw({
                fill: options.fill,
                stroke: options.stroke,
                startCap: options.startCap,
                endCap: options.endCap
            });
        }
    }
    /**
     * Returns a clone of this connection.
     * @returns A new Connection instance that is a copy of this connection
     */
    clone() {
        const json = this.serialize();
        if (this.diagram && this.diagram._isEditable && defined(this.dataItem)) {
            json.options.dataItem = this.diagram.options.cloneDataItem(this.dataItem);
        }
        return new Connection(this.from, this.to, json.options);
    }
    /**
     * Returns a serialized connection in JSON format. Consists of the options and the dataItem.
     * @returns Serialized connection object containing options, endpoints, and data
     */
    serialize() {
        const from = this.from.toJSON ? this.from.toJSON : this.from.toString(), to = this.to.toJSON ? this.to.toJSON : this.to.toString();
        const json = deepExtend({}, {
            options: this.options,
            from: from,
            to: to
        });
        if (defined(this.dataItem)) {
            json.dataItem = this.dataItem.toString();
        }
        json.options.points = this.points();
        if (this.locks && this.locks.length > 0) {
            json.options.locks = this.locks.map(lock => ({ ...lock }));
        }
        return json;
    }
    /**
     * @hidden
     *
     * Returns whether the given Point or Rect hits this connection.
     *
     * @param value
     * @returns {Connection}
     * @private
     */
    _hitTest(value) {
        if (this.visible()) {
            const p = new Point(value.x, value.y), from = this.sourcePoint(), to = this.targetPoint();
            if (value.isEmpty && !value.isEmpty() && value.contains(from) && value.contains(to)) {
                return this;
            }
            if (this._router.hitTest(p)) {
                return this;
            }
        }
    }
    /** @hidden */
    _hover(value) {
        let color = (this.options.stroke || {}).color;
        if (value && isDefined(this.options.hover.stroke.color)) {
            color = this.options.hover.stroke.color;
        }
        this.path.redraw({
            stroke: {
                color: color
            }
        });
    }
    /** @hidden */
    _refreshPath() {
        if (!defined(this.path)) {
            return;
        }
        this._drawPath();
        this.bounds(this._router.getBounds());
    }
    /** @hidden */
    _drawPath() {
        if (this._router) {
            this._router.route(); // sets the intermediate points
        }
        const source = this.sourcePoint();
        const target = this.targetPoint();
        const points = this.points();
        this.path.redraw({
            points: [source].concat(points, [target])
        });
    }
    /** @hidden */
    _clearSourceConnector() {
        this.sourceConnector = undefined;
        this._resolvedSourceConnector = undefined;
    }
    /** @hidden */
    _clearTargetConnector() {
        this.targetConnector = undefined;
        this._resolvedTargetConnector = undefined;
    }
    /** @hidden */
    _removeFromSourceConnector() {
        if (this.sourceConnector) {
            remove(this.sourceConnector.connections, this);
        }
    }
    /** @hidden */
    _removeFromTargetConnector() {
        if (this.targetConnector) {
            remove(this.targetConnector.connections, this);
        }
    }
    /**
     * Converts the connection to a JSON representation for serialization.
     * @returns Object containing the connection's endpoints information
     */
    toJSON() {
        let from, to, point;
        if (this.from && this.from.toJSON) {
            from = this.from.toJSON();
        }
        else {
            point = this._sourcePoint;
            from = {
                x: point.x,
                y: point.y
            };
        }
        if (this.to && this.to.toJSON) {
            to = this.to.toJSON();
        }
        else {
            point = this._targetPoint;
            to = {
                x: point.x,
                y: point.y
            };
        }
        return {
            from: from,
            to: to
        };
    }
    /** @hidden */
    focus() {
        this.path.drawingElement.options.set(CLASS_NAME, FOCUS_CLASS);
    }
    /** @hidden */
    blur() {
        this.path.drawingElement.options.set(CLASS_NAME, EMPTY);
    }
    /** @hidden */
    pointsEditable() {
        let editable = this.options.editable;
        if (editable && typeof editable !== 'boolean') {
            editable = editable.points;
        }
        return editable !== false;
    }
}
